/**
 * Workflow Guards - Frontend utilities for workflow separation
 * Ensures proper routing between college and university pool workflows
 */

/**
 * Check if user can access pool workflow
 * @param {Object} user - User object with certification status
 * @returns {boolean} True if user has university certification
 */
export function canAccessPoolWorkflow(user) {
  if (!user) return false;
  return user.university_certified === true || user.university_certified === 1;
}

/**
 * Get correct route for paper access based on workflow type
 * @param {number} paperId - Paper ID
 * @param {string} workflowType - Workflow type ('college' or 'pool')
 * @returns {string} Route path
 */
export function getPaperRoute(paperId, workflowType) {
  if (workflowType === 'pool') {
    return `/university-pool/papers/${paperId}`;
  }
  return `/papers/${paperId}`;
}

/**
 * Get correct route for paper list based on workflow type
 * @param {string} workflowType - Workflow type ('college' or 'pool')
 * @returns {string} Route path
 */
export function getPaperListRoute(workflowType) {
  if (workflowType === 'pool') {
    return '/university-pool/papers';
  }
  return '/papers';
}

/**
 * Check if current path is a pool workflow route
 * @param {string} path - Current route path
 * @returns {boolean} True if path is a pool route
 */
export function isPoolWorkflowRoute(path) {
  return path.startsWith('/university-pool');
}

/**
 * Check if current path is a college workflow route
 * @param {string} path - Current route path
 * @returns {boolean} True if path is a college route
 */
export function isCollegeWorkflowRoute(path) {
  // College routes are any routes that are not pool routes
  return !isPoolWorkflowRoute(path);
}

/**
 * Redirect to correct workflow if needed
 * @param {Object} user - User object
 * @param {string} currentPath - Current route path
 * @returns {string|null} Redirect path or null if no redirect needed
 */
export function checkWorkflowRedirect(user, currentPath) {
  // If user is trying to access pool routes without certification
  if (isPoolWorkflowRoute(currentPath) && !canAccessPoolWorkflow(user)) {
    return '/papers'; // Redirect to college papers
  }

  // No redirect needed
  return null;
}

/**
 * Get workflow type from current path
 * @param {string} path - Current route path
 * @returns {string} Workflow type ('college' or 'pool')
 */
export function getWorkflowTypeFromPath(path) {
  return isPoolWorkflowRoute(path) ? 'pool' : 'college';
}

/**
 * Format workflow-aware error message
 * @param {Object} error - Error object from API
 * @param {string} currentWorkflow - Current workflow type
 * @returns {string} Formatted error message
 */
export function formatWorkflowError(error, currentWorkflow) {
  if (error.workflow && error.suggestion) {
    return `${error.message}\n\nSuggestion: ${error.suggestion}`;
  }
  
  if (error.message && error.message.includes('not found')) {
    const otherWorkflow = currentWorkflow === 'college' ? 'university pool' : 'college';
    return `${error.message}\n\nThis resource may belong to the ${otherWorkflow} workflow.`;
  }

  return error.message || 'An error occurred';
}

/**
 * Get navigation items based on user certification
 * @param {Object} user - User object
 * @returns {Array} Array of navigation items
 */
export function getWorkflowNavigationItems(user) {
  const items = [
    {
      label: 'College Papers',
      path: '/papers',
      workflow: 'college',
      icon: 'document'
    }
  ];

  // Add pool navigation if user is certified
  if (canAccessPoolWorkflow(user)) {
    items.push({
      label: 'University Pool',
      path: '/university-pool/papers',
      workflow: 'pool',
      icon: 'university'
    });
  }

  return items;
}

/**
 * Validate paper ID for workflow
 * @param {number} paperId - Paper ID
 * @param {string} workflowType - Expected workflow type
 * @param {Function} apiCall - API call function to validate
 * @returns {Promise<boolean>} True if paper exists in workflow
 */
export async function validatePaperWorkflow(paperId, workflowType, apiCall) {
  try {
    await apiCall(paperId, workflowType);
    return true;
  } catch (error) {
    if (error.workflow && error.workflow !== workflowType) {
      return false;
    }
    throw error;
  }
}
